/**
 * Book-level narration metadata (palette, narrator color, book RMS).
 * Stored in `book.metadata.narration`.
 */

import {
  getNarratorColor,
  normalizeSpeakerColorPair,
  parseSpeakerPaletteId,
  type SpeakerColorPair,
  type SpeakerPaletteId,
} from "./speakerPalette";
import type { BookCharacter } from "./characterRoster";
import { assignSpeakerColor } from "./speakerPalette";

export type BookNarrationSettings = {
  narratorColor: SpeakerColorPair;
  paletteId: SpeakerPaletteId;
  customPalette?: Record<string, SpeakerColorPair>;
  /** Running average of mastered chapter RMS for book-level consistency. */
  bookMeanRmsDb?: number;
  narratorName?: string;
};

export function defaultBookNarration(paletteId: SpeakerPaletteId = "pastel"): BookNarrationSettings {
  return {
    narratorColor: getNarratorColor(paletteId),
    paletteId,
  };
}

export function getNarrationFromBookMetadata(
  metadata: Record<string, unknown> | null | undefined
): BookNarrationSettings {
  const raw = metadata?.narration;
  if (!raw || typeof raw !== "object") return defaultBookNarration();
  const rec = raw as Record<string, unknown>;
  const paletteId = parseSpeakerPaletteId(rec.paletteId);
  const narratorColor =
    normalizeSpeakerColorPair(rec.narratorColor) ?? getNarratorColor(paletteId);
  let customPalette: Record<string, SpeakerColorPair> | undefined;
  if (rec.customPalette && typeof rec.customPalette === "object") {
    customPalette = {};
    for (const [key, value] of Object.entries(rec.customPalette as Record<string, unknown>)) {
      const pair = normalizeSpeakerColorPair(value);
      if (pair) customPalette[key] = pair;
    }
    if (Object.keys(customPalette).length === 0) customPalette = undefined;
  }
  const bookMeanRmsDb =
    typeof rec.bookMeanRmsDb === "number" && Number.isFinite(rec.bookMeanRmsDb)
      ? rec.bookMeanRmsDb
      : undefined;
  const narratorName =
    typeof rec.narratorName === "string" && rec.narratorName.trim()
      ? rec.narratorName.trim()
      : undefined;
  return {
    narratorColor,
    paletteId,
    customPalette,
    bookMeanRmsDb,
    narratorName,
  };
}

export function withNarrationInMetadata(
  metadata: Record<string, unknown>,
  narration: BookNarrationSettings
): Record<string, unknown> {
  return {
    ...metadata,
    narration: {
      narratorColor: narration.narratorColor,
      paletteId: narration.paletteId,
      ...(narration.customPalette ? { customPalette: narration.customPalette } : {}),
      ...(typeof narration.bookMeanRmsDb === "number"
        ? { bookMeanRmsDb: narration.bookMeanRmsDb }
        : {}),
      ...(narration.narratorName ? { narratorName: narration.narratorName } : {}),
    },
  };
}

/**
 * Ensure every character has a color pair for the active palette.
 * Preserves existing colors when still valid; assigns new ones without reshuffling ids.
 */
export function ensureCharacterColors(
  characters: BookCharacter[],
  paletteId: SpeakerPaletteId
): BookCharacter[] {
  const used = new Set<number>();
  return characters.map((c) => {
    if (c.color?.highlight && c.color?.text) {
      return c;
    }
    const color = assignSpeakerColor(c.id, paletteId, used);
    return { ...c, color, updatedAt: new Date().toISOString() };
  });
}

export function resolveSpeakerColor(
  speakerId: string | "narrator",
  characters: BookCharacter[],
  narration: BookNarrationSettings
): SpeakerColorPair {
  if (speakerId === "narrator") {
    return narration.customPalette?.narrator ?? narration.narratorColor;
  }
  const custom = narration.customPalette?.[speakerId];
  if (custom) return custom;
  const character = characters.find((c) => c.id === speakerId);
  if (character?.color) return character.color;
  return assignSpeakerColor(speakerId, narration.paletteId);
}
